import type { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { baseApi } from '@shared/api/baseApi';
import type { ApiMessage } from '@shared/api/response';
import type {
  ClientDetail,
  ClientGroup,
  ClientGroupWithCount,
  ClientInput,
  ClientSummary,
  CodePreview,
  EntityType,
  PageResponse,
} from '@shared/api/types';
import { fileNameFrom, type DownloadedFile } from '@shared/lib/download';

export type ArchivedFilter = 'active' | 'archived' | 'all';

export interface ClientFilters {
  q?: string | undefined;
  entityType?: EntityType | undefined;
  groupId?: string | undefined; // a group id, or 'none'
  archived: ArchivedFilter;
  /** Only the clients in this calendar year (added there or rolled over into it). */
  year?: number | undefined;
}

export interface ClientListParams extends ClientFilters {
  page: number;
  pageSize: number;
  sort?: string | undefined;
}

const clean = (f: ClientFilters) => ({
  q: f.q === '' ? undefined : f.q,
  entityType: f.entityType,
  groupId: f.groupId,
  archived: f.archived,
  year: f.year,
});

/** Screens built from a client's details and services (Services registers, profiles, the
 *  client View, the year lists): a client change refreshes them too. */
const SERVICE_VIEWS = ['Register', 'Profile', 'ClientView', 'ClientYear'] as const;

const clientsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listClients: build.query<PageResponse<ClientSummary>, ClientListParams>({
      query: ({ page, pageSize, sort, ...f }) => ({
        url: '/clients',
        params: { ...clean(f), page, pageSize, sort },
      }),
      providesTags: (res) => [
        { type: 'Client', id: 'LIST' },
        ...(res?.items.map((c) => ({ type: 'Client' as const, id: c.id })) ?? []),
      ],
    }),
    getClient: build.query<ClientDetail, string>({
      query: (id) => `/clients/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Client', id }],
    }),
    /** `year`: the calendar year picked in the header; the client starts there. */
    createClient: build.mutation<ApiMessage<ClientDetail>, { body: ClientInput; year: number }>({
      query: ({ body, year }) => ({ url: '/clients', method: 'POST', body, params: { year } }),
      invalidatesTags: [{ type: 'Client', id: 'LIST' }, 'ClientGroup', 'Audit', 'Task', ...SERVICE_VIEWS],
    }),
    updateClient: build.mutation<ApiMessage<ClientDetail>, { id: string; body: ClientInput }>({
      query: ({ id, body }) => ({ url: `/clients/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Client', id },
        { type: 'Client', id: 'LIST' },
        'ClientGroup',
        'Audit',
        'Task',
        ...SERVICE_VIEWS,
      ],
    }),
    setClientArchived: build.mutation<ApiMessage<ClientDetail>, { id: string; archived: boolean }>({
      query: ({ id, archived }) => ({
        url: `/clients/${id}/${archived ? 'archive' : 'unarchive'}`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Client', id },
        { type: 'Client', id: 'LIST' },
        'ClientGroup', // a group's active-client count changes
        'Audit',
        'Task',
        ...SERVICE_VIEWS,
      ],
    }),
    codePreview: build.query<
      CodePreview,
      { entityType: EntityType; last4: string; name?: string; tpLast?: string; excludeId?: string }
    >({
      query: (params) => ({ url: '/clients/code-preview', params }),
    }),
    exportClients: build.mutation<DownloadedFile, ClientFilters & { format: 'csv' | 'xlsx' | 'pdf' }>({
      query: ({ format, ...f }) => ({
        url: '/clients/export',
        params: { ...clean(f), format },
        responseHandler: (r) => r.blob(),
        cache: 'no-cache',
      }),
      transformResponse: (blob: Blob, meta, { format }) => ({
        blob,
        fileName: fileNameFrom(
          (meta as FetchBaseQueryMeta | undefined)?.response?.headers.get('content-disposition') ?? null,
          `COMPLIANCE-DESK_CLIENT-MASTER.${format}`,
        ),
      }),
    }),
    clientGroups: build.query<ClientGroupWithCount[], undefined>({
      query: () => '/client-groups',
      providesTags: ['ClientGroup'],
    }),
    createGroup: build.mutation<ApiMessage<ClientGroup>, { name: string }>({
      query: (body) => ({ url: '/client-groups', method: 'POST', body }),
      invalidatesTags: ['ClientGroup', 'Audit'],
    }),
    renameGroup: build.mutation<ApiMessage<ClientGroup>, { id: string; name: string }>({
      query: ({ id, name }) => ({ url: `/client-groups/${id}`, method: 'PATCH', body: { name } }),
      invalidatesTags: ['ClientGroup', { type: 'Client', id: 'LIST' }, 'Audit'],
    }),
    setGroupArchived: build.mutation<ApiMessage<ClientGroup>, { id: string; archived: boolean }>({
      query: ({ id, archived }) => ({
        url: `/client-groups/${id}/${archived ? 'archive' : 'unarchive'}`,
        method: 'POST',
      }),
      invalidatesTags: ['ClientGroup', 'Audit'],
    }),
  }),
});

export const {
  useListClientsQuery,
  useGetClientQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useSetClientArchivedMutation,
  useCodePreviewQuery,
  useExportClientsMutation,
  useClientGroupsQuery,
  useCreateGroupMutation,
  useRenameGroupMutation,
  useSetGroupArchivedMutation,
} = clientsApi;
